import * as React from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { cva, type VariantProps } from 'class-variance-authority'

type TabsProps = Omit<TabsPrimitive.Root.Props, 'onValueChange'> & {
  onValueChange?: (value: any) => void
}

function Tabs({ className, orientation = 'horizontal', onValueChange, ...props }: TabsProps) {
  return (
    <TabsPrimitive.Root
      data-slot='tabs'
      data-orientation={orientation}
      className={cn('group/tabs flex gap-2 data-horizontal:flex-col', className)}
      onValueChange={(value) => onValueChange?.(value)}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  'border-b-border border-b group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-horizontal/tabs:h-9 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none',
  {
    variants: {
      variant: {
        default: 'bg-muted',
        line: 'gap-1 bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function TabsList({
  className,
  variant = 'line',
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <ScrollArea className='h-10'>
      <TabsPrimitive.List
        data-slot='tabs-list'
        data-variant={variant}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      />
    </ScrollArea>
  )
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot='tabs-trigger'
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
        'data-active:bg-background data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground',
        'after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-5px] group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
        className
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot='tabs-content'
      className={cn('flex-1 text-sm outline-none', className)}
      {...props}
    />
  )
}

type ViewTab = {
  key: string
  translateKey: React.ReactNode
  visible?: boolean
  permission?: unknown
  component?: React.ReactNode
}

type ViewTabsProps = {
  defaultKey: string
  activeTab?: string
  onChange?: (value: string) => void
  tabs: ViewTab[]
  permissions?: unknown[]
  hasPermission?: (...args: any[]) => boolean
  className?: string
}

function ViewTabs({
  defaultKey,
  activeTab,
  onChange,
  tabs,
  permissions,
  hasPermission,
  className,
}: ViewTabsProps) {
  const visibleTabs = React.useMemo(() => {
    return tabs.filter((tab) => {
      if (tab.visible === false) return false
      if (tab.permission == null || !permissions || !hasPermission) return true
      return hasPermission(permissions, tab.permission)
    })
  }, [hasPermission, permissions, tabs])

  if (visibleTabs.length === 0) {
    return null
  }

  const fallbackTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : defaultKey

  return (
    <Tabs
      className={cn('w-full', className)}
      value={activeTab}
      defaultValue={fallbackTab}
      onValueChange={(value) => onChange?.(String(value))}
    >
      <TabsList
        variant='line'
        className='w-full justify-start rounded-none border-b border-border p-0'
      >
        {visibleTabs.map((tab) => (
          <TabsTrigger key={tab.key} value={tab.key} className='rounded-none px-4 py-2'>
            {tab.translateKey}
          </TabsTrigger>
        ))}
      </TabsList>

      {visibleTabs.map((tab) => (
        <TabsContent key={tab.key} value={tab.key} className='pt-4'>
          {tab.component}
        </TabsContent>
      ))}
    </Tabs>
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, ViewTabs, tabsListVariants }
